import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
import { useApi, useToaster } from "@shopexperts/shared";
import type { ExpertFilterPreset, ExpertFilterSnapshot } from "@shopexperts/legacy-api";

const PRESETS_QUERY_KEY = ["admin", "expert-filter-presets"];

/** Presets are per-admin; another admin's preset ID 404s, so there is nothing to share. */
export function useExpertFilterPresets() {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: PRESETS_QUERY_KEY,
    queryFn: async () => await api.admin.expertManager.listFilterPresets(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const presets = computed<ExpertFilterPreset[]>(() => data.value ?? []);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PRESETS_QUERY_KEY });

  const createPreset = useMutation({
    mutationFn: async (body: { name: string; filters: ExpertFilterSnapshot }) => {
      return await api.admin.expertManager.createFilterPreset({ body });
    },
    onSuccess: (preset) => {
      toaster.create({ title: `Saved "${preset.name}"`, type: "success" });
      invalidate();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to save preset: ${error.message}`, type: "error" });
    },
  });

  const updatePreset = useMutation({
    mutationFn: async ({ presetId, ...body }: { presetId: number; name: string; filters: ExpertFilterSnapshot }) => {
      return await api.admin.expertManager.updateFilterPreset({ params: { presetId }, body });
    },
    onSuccess: (preset) => {
      toaster.create({ title: `Updated "${preset.name}"`, type: "success" });
      invalidate();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to update preset: ${error.message}`, type: "error" });
    },
  });

  const deletePreset = useMutation({
    mutationFn: async (presetId: number) => {
      return await api.admin.expertManager.deleteFilterPreset({ params: { presetId } });
    },
    onSuccess: () => {
      toaster.create({ title: "Preset deleted", type: "success" });
      invalidate();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to delete preset: ${error.message}`, type: "error" });
    },
  });

  return { presets, isLoading, error, refetch, createPreset, updatePreset, deletePreset };
}
